import { createSelector } from 'reselect';

import { PaginationType } from 'constants/Global';
import { ALL_ROLES, INTEGRATION, READ_ONLY, USER_STATUS, USER_WARNINGS } from 'constants/UserConstants';
import { getUsersFilter } from 'selectors/userSelectors';

const sortByUserName = (a, b) => a.displayName?.localeCompare(b.displayName);

/** @returns {import('../reducer').UserReducer} */
export const getState = ({ newUsers }) => newUsers;

/** @returns {string} */
const getId = (_, id) => id;

const getData = createSelector(getState, ({ data }) => data);

export const getUsers = createSelector(getData, ({ byId }) => byId);

const getUsersSelect = createSelector(getData, ({ select }) => select);

export const getUsersBySelectId = createSelector(
  getUsers,
  getUsersSelect,
  getId,
  (users, userSelect, id) =>
    /** @type {Array<import('../reducer/data/byId').UserDataByIdEntity>} */ (
      userSelect[id]?.map(userId => users?.[userId] ?? {}).sort(sortByUserName) ?? []
    )
);

export const getUserById = createSelector(
  getUsers,
  getId,
  (users, userId) => /** @type {import('../reducer/data/byId').UserDataByIdEntity} */ (users?.[userId] ?? {})
);

export const getUserByIds = createSelector(getUsers, getId, (users, userIds = []) =>
  userIds.map(userId => users?.[userId]).filter(Boolean));

const getUi = createSelector(getState, ({ ui }) => ui);

export const computeWarnings = warnings =>
  Object.values(USER_WARNINGS).reduce((acc, key) => {
    if (key in warnings && Boolean(warnings[key])) {
      return { ...acc, [key]: warnings[key] };
    }
    return acc;
  }, {});

export const getUpdateUserWarnings = createSelector(getUi, ui => ui.warnings);

const getSortedWarnings = createSelector(getUpdateUserWarnings, warnings => computeWarnings(warnings));

export const hasWarning = createSelector(getSortedWarnings, warnings => Object.keys(warnings).length > 0);

export const getFirstWarning = createSelector(getSortedWarnings, warnings => Object.entries(warnings)[0]);

export const isCheckingUser = createSelector(getUi, ui => ui.isCheckingUser);

const getUsersUiSelect = createSelector(getUi, ({ select }) => select);

export const getUsersSelectIsSearching = createSelector(
  getUsersUiSelect,
  getId,
  ({ searching }, id) => searching?.[id] ?? false
);

export const getUsersSelectIsLoading = createSelector(
  getUsersUiSelect,
  getId,
  ({ loading }, id) => loading?.[id] ?? false
);

export const getUsersSelectPage = createSelector(
  getUsersUiSelect,
  getId,
  ({ page }, selectId) => page?.[selectId]?.page?.number
);

export const getUsersSelectHasNext = createSelector(
  getUsersUiSelect,
  getId,
  ({ page }, selectId) => page?.[selectId]?.hasMore ?? false
);

const getDashboard = createSelector(getUi, ui => ui.dashboard);

const getPage = createSelector(getDashboard, dashboard => dashboard.page);

export const hasNext = createSelector(getDashboard, dashboard => dashboard.hasMore);

export const getParams = createSelector(getUsersFilter, getPage, (filters, page) => ({
  ...filters,
  page: page.number,
  size: 20,
  paginationType: PaginationType.SLICE,
}));

export const isPatchingUser = createSelector(getUi, ui => ui.isPatching);

export const isPasswordModalOpen = createSelector(getUi, ui => ui.showPasswordModal);

const getImpersonateState = createSelector(getUi, ui => ui.impersonate);

export const getImpersonatePager = createSelector(getImpersonateState, impersonate => impersonate.data.page);

export const isImpersonateLoading = createSelector(getImpersonateState, impersonate => impersonate.data.loading);

export const getImpersonateStateUserId = createSelector(getImpersonateState, impersonate => impersonate.data.ids);

export const getImpersonateFilters = createSelector(getImpersonateState, state => state.filters);

export const getImpersonateModalParameters = createSelector(getImpersonateFilters, filters => {
  const [_, ...DEFAULT_ROLES] = ALL_ROLES;

  return {
    page: 0,
    size: 20,
    rolesNot: [INTEGRATION],
    status: [USER_STATUS.ACTIVE],
    fields: 'id,displayName,dealerIds,groupId,email,roles,firstName,lastName',
    ...filters,
    roles: filters.roles.length === 0 ? DEFAULT_ROLES : filters.roles,
  };
});

export const isImpersonateModalOpen = createSelector(getImpersonateState, state => state.open);

export const isReadonlyUser = createSelector(getUserById, user => user?.roles?.includes(READ_ONLY));

export * from './dashboardPreferences';
